<script setup>
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { onMounted, ref } from 'vue'
import { useAuthStore } from '@/stores/authStore.js'
import { userApi } from '@/api/User/UserApi.js'
import LoadingSpinner from '@/components/Spinners/LoadingSpinner.vue'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import PasswordSettingsPanel from '@/views/Admin/PortalSettings/PasswordSettingsPanel.vue'
import { Format as FormatUtils } from '@/models/utils/FormatUtils.js'
import InactiveAccountsSettingsPanel from '@/views/Admin/PortalSettings/InactiveAccountsSettingsPanel.vue'

const tenant = ref(null)
const tenantSettings = ref({})
const isLoading = ref(true)
const showPasswordExpirationSettings = ref(false)
const showInactiveAccountsSettings = ref(false)
const passwordExpirationEnabled = ref(false)
const passwordExpirationSubtitle = ref(null)
const inactiveAccountsEnabled = ref(false)
const inactiveAccountsSubtitle = ref(null)

onMounted(() => {
	loadSettings()
})

function loadSettings()
{
	isLoading.value = true

	userApi.getTenantById(useAuthStore().getAuth.user().tenant_id, (tenantData) => {
		tenant.value = tenantData;
		tenantSettings.value = JSON.parse(tenant.value.settings_json);

		setPasswordExpirationSubtitle()
		setInactiveAccountsSubtitle()

		isLoading.value = false;
	})
}

function setPasswordExpirationSubtitle()
{
	passwordExpirationEnabled.value = tenantSettings.value.passwords.expiration_enabled.value
	const passwordExpirationDays = tenantSettings.value.passwords.expiration_days.value
	const loginWarningDays = tenantSettings.value.passwords.login_warning_days.value

	let selectedReminderDays = tenantSettings.value.passwords.reminder_email_days.value

	if (selectedReminderDays.length > 0)
	{
		selectedReminderDays.sort((a, b) => b - a)
		const formatter = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });
		selectedReminderDays = formatter.format(selectedReminderDays.map(day => day.toString()))
		selectedReminderDays = `∙ Reminder emails ${selectedReminderDays} day(s) before `
	}
	else
	{
		selectedReminderDays = ''
	}

	if (passwordExpirationEnabled.value)
	{
		passwordExpirationSubtitle.value = `Expire after ${passwordExpirationDays} ${FormatUtils.pluralize('day', passwordExpirationDays)} ${selectedReminderDays}∙ Log in warning ${loginWarningDays} ${FormatUtils.pluralize('day', loginWarningDays)} before`
	}
}

function setInactiveAccountsSubtitle()
{
	inactiveAccountsEnabled.value = tenantSettings.value.inactive_accounts.disable_enabled.value
	const lastLoginDays = tenantSettings.value.inactive_accounts.last_login_days.value
	const warningDays = tenantSettings.value.inactive_accounts.warning_days.value
	const reEnabledGraceDays = tenantSettings.value.inactive_accounts.reenabled_grace_days.value

	if (inactiveAccountsEnabled.value)
	{
		inactiveAccountsSubtitle.value = `Disable after ${lastLoginDays} ${FormatUtils.pluralize('day', lastLoginDays)} without a log in ∙ Warning email ${warningDays} ${FormatUtils.pluralize('day', warningDays)} before ∙ ${reEnabledGraceDays} ${FormatUtils.pluralize('day', reEnabledGraceDays)} grace period after re-enabling`
	}
}

function onPasswordSettingsDismiss(refresh)
{
	showPasswordExpirationSettings.value = false

	if (refresh)
	{
		loadSettings()
	}
}

function onInactiveAccountSettingsDismiss(refresh)
{
	showInactiveAccountsSettings.value = false

	if (refresh)
	{
		loadSettings()
	}
}
</script>

<template>
	<div v-if="!showPasswordExpirationSettings && !showInactiveAccountsSettings" class="flex flex-col rounded-lg border border-neutral-subtler border-solid bg-neutral p-6 card-shadow">
		<div class="flex min-h-15">
			<div class="flex flex-col flex-grow space-y-1">
				<span class="text-xl font-semibold text-heading">Password Expiration</span>
				<LoadingSpinner v-if="isLoading" size="12" class="w-fit mr-auto" padding-classes="" />
				<span v-if="!isLoading && passwordExpirationEnabled" class="text-sm font-medium text-grayscale-body">{{ passwordExpirationSubtitle }}</span>
				<span v-if="!isLoading && !passwordExpirationEnabled" class="text-sm font-medium text-grayscale-body">Password expiration is currently <span class="font-bold text-error">off</span>.</span>
			</div>
			<SubmitButton class="my-auto ml-2" :label="passwordExpirationEnabled ? 'Edit' : 'Enable'" :type="ButtonType.Outline" :size="ButtonSize.Small" :disabled="isLoading" @click.prevent="showPasswordExpirationSettings = !showPasswordExpirationSettings" />
		</div>
		<div class="bg-neutral-subtler w-full h-[1px] my-5"/>
		<div class="flex min-h-15">
			<div class="flex flex-col flex-grow space-y-1">
				<span class="text-xl font-semibold text-heading">Inactive Accounts</span>
				<LoadingSpinner v-if="isLoading" size="12" class="w-fit mr-auto" padding-classes="" />

				<span v-if="!isLoading && inactiveAccountsEnabled" class="text-sm font-medium text-grayscale-body">{{ inactiveAccountsSubtitle }}</span>
				<span v-if="!isLoading && !inactiveAccountsEnabled" class="text-sm font-medium text-grayscale-body">Inactive account disabling is currently <span class="font-bold text-error">off</span>.</span>
			</div>
			<SubmitButton class="my-auto ml-2" :label="inactiveAccountsEnabled ? 'Edit' : 'Enable'" :type="ButtonType.Outline" :size="ButtonSize.Small" :disabled="isLoading" @click.prevent="showInactiveAccountsSettings = !showInactiveAccountsSettings" />
		</div>
	</div>

	<PasswordSettingsPanel v-if="showPasswordExpirationSettings" @dismiss="onPasswordSettingsDismiss" />
	<InactiveAccountsSettingsPanel v-if="showInactiveAccountsSettings" @dismiss="onInactiveAccountSettingsDismiss" />
</template>

<style scoped>

</style>